/**
 * Tenant Invites Tab Component
 * Admin/Gestor tab to create and manage invite codes
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/state/session";
import { Plus, Copy, Check, X, Clock, UserPlus } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

type InviteStatus = "pending" | "accepted" | "revoked" | "expired";

interface Invite {
  id: string;
  invite_code: string;
  role: "admin" | "gestor" | "funcionario";
  status: InviteStatus;
  max_uses: number | null;
  uses_count: number;
  expires_at: string;
  created_at: string;
  used_at: string | null;
}

export function InvitesTab() {
  const { toast } = useToast();
  const { user } = useSession();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Form state
  const [selectedRole, setSelectedRole] = useState<"funcionario" | "gestor">("funcionario");
  const [expirationDays, setExpirationDays] = useState("7");

  // Load tenant ID
  useEffect(() => {
    const loadTenantId = async () => {
      if (!user) return;
      const { data } = await supabase
        .from("tenant_users")
        .select("tenant_id")
        .eq("user_id", user.id)
        .single();
      if (data) setTenantId(data.tenant_id);
    };
    loadTenantId();
  }, [user]);

  useEffect(() => {
    if (tenantId) loadInvites();
  }, [tenantId]);

  const loadInvites = async () => {
    if (!tenantId) return;

    const { data, error } = await supabase
      .from("tenant_invites")
      .select("*")
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false });

    if (error) {
      toast({
        title: "Erro ao carregar convites",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setInvites((data || []) as Invite[]);
  };

  const handleCreateInvite = async () => {
    if (!tenantId || !user) return;

    setLoading(true);
    try {
      // Gerar código único
      const { data: codeData, error: codeError } = await supabase.rpc("generate_invite_code");

      if (codeError) throw codeError;

      const inviteCode = codeData as string;
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + parseInt(expirationDays));

      // Criar convite
      const { error: insertError } = await supabase.from("tenant_invites").insert({
        tenant_id: tenantId,
        created_by: user.id,
        invite_code: inviteCode,
        role: selectedRole,
        max_uses: 1,
        expires_at: expiresAt.toISOString(),
      });

      if (insertError) throw insertError;

      toast({
        title: "✅ Convite criado!",
        description: `Código: ${inviteCode}`,
      });

      loadInvites();
    } catch (error: any) {
      toast({
        title: "Erro ao criar convite",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (code: string) => {
    const inviteUrl = `${window.location.origin}/auth?mode=signup&invite=${code}`;
    navigator.clipboard.writeText(inviteUrl);
    setCopiedCode(code);
    toast({
      title: "Link copiado!",
      description: "Link de convite copiado para a área de transferência",
    });
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleRevokeInvite = async (id: string) => {
    const { error } = await supabase
      .from("tenant_invites")
      .update({ status: "revoked" })
      .eq("id", id);

    if (error) {
      toast({
        title: "Erro ao revogar convite",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Convite revogado",
      description: "O código não poderá mais ser utilizado",
    });

    loadInvites();
  };

  const getStatusBadge = (status: InviteStatus) => {
    const variants = {
      pending: { variant: "default" as const, label: "Pendente", icon: Clock },
      accepted: { variant: "default" as const, label: "Aceito", icon: Check },
      revoked: { variant: "destructive" as const, label: "Cancelado", icon: X },
      expired: { variant: "secondary" as const, label: "Expirado", icon: Clock },
    };

    const config = variants[status];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getRoleBadge = (role: string) => {
    const labels = {
      admin: "Administrador",
      gestor: "Gestor",
      funcionario: "Funcionário",
    };

    return <Badge variant="outline">{labels[role as keyof typeof labels] || role}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Create Invite Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Criar Novo Convite
          </CardTitle>
          <CardDescription>
            Gere um código de convite para adicionar usuários ao seu tenant
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="role">Função</Label>
              <Select value={selectedRole} onValueChange={(v: any) => setSelectedRole(v)}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="funcionario">Funcionário</SelectItem>
                  <SelectItem value="gestor">Gestor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiration">Validade (dias)</Label>
              <Select value={expirationDays} onValueChange={setExpirationDays}>
                <SelectTrigger id="expiration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 dia</SelectItem>
                  <SelectItem value="3">3 dias</SelectItem>
                  <SelectItem value="7">7 dias</SelectItem>
                  <SelectItem value="14">14 dias</SelectItem>
                  <SelectItem value="30">30 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button onClick={handleCreateInvite} disabled={loading} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Gerar Código
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invites List */}
      <Card>
        <CardHeader>
          <CardTitle>Convites Criados</CardTitle>
          <CardDescription>
            Gerencie os códigos de convite do seu tenant
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invites.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <UserPlus className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum convite criado ainda</p>
              <p className="text-sm">Crie um convite para adicionar usuários ao seu tenant</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Função</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Usos</TableHead>
                    <TableHead>Expira em</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((invite) => (
                    <TableRow key={invite.id}>
                      <TableCell className="font-mono font-bold">
                        {invite.invite_code}
                      </TableCell>
                      <TableCell>{getRoleBadge(invite.role)}</TableCell>
                      <TableCell>{getStatusBadge(invite.status)}</TableCell>
                      <TableCell>
                        {invite.uses_count} / {invite.max_uses || "∞"}
                      </TableCell>
                      <TableCell>
                        {format(new Date(invite.expires_at), "dd/MM/yyyy", {
                          locale: ptBR,
                        })}
                      </TableCell>
                      <TableCell>
                        {format(new Date(invite.created_at), "dd/MM/yyyy HH:mm", {
                          locale: ptBR,
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {invite.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCopyCode(invite.invite_code)}
                              >
                                {copiedCode === invite.invite_code ? (
                                  <Check className="h-4 w-4" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRevokeInvite(invite.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
